import { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import ProtectedAdminRoute from '@/components/ProtectedAdminRoute';

const HomePage = lazy(() => import('@/pages/HomePage').then((m) => ({ default: m.HomePage })));
const ForumsPage = lazy(() => import('@/pages/ForumsPage').then((m) => ({ default: m.ForumsPage })));
const ForumCategoryPage = lazy(() =>
  import('@/pages/ForumCategoryPage').then((m) => ({ default: m.ForumCategoryPage }))
);
const PostPage = lazy(() => import('@/pages/PostPage').then((m) => ({ default: m.PostPage })));
const CreatePostPage = lazy(() =>
  import('@/pages/CreatePostPage').then((m) => ({ default: m.CreatePostPage }))
);
const EditPostPage = lazy(() =>
  import('@/pages/EditPostPage').then((m) => ({ default: m.EditPostPage }))
);
const VisaExperiencesPage = lazy(() =>
  import('@/pages/VisaExperiencesPage').then((m) => ({ default: m.VisaExperiencesPage }))
);
const VisaExperienceDetailPage = lazy(() =>
  import('@/pages/VisaExperienceDetailPage').then((m) => ({ default: m.VisaExperienceDetailPage }))
);
const CreateVisaExperiencePage = lazy(() =>
  import('@/pages/CreateVisaExperiencePage').then((m) => ({ default: m.CreateVisaExperiencePage }))
);
const EditVisaExperiencePage = lazy(() =>
  import('@/pages/EditVisaExperiencePage').then((m) => ({ default: m.EditVisaExperiencePage }))
);
const DirectoryPage = lazy(() =>
  import('@/pages/DirectoryPage').then((m) => ({ default: m.DirectoryPage }))
);
const BusinessDetailPage = lazy(() =>
  import('@/pages/BusinessDetailPage').then((m) => ({ default: m.BusinessDetailPage }))
);
const EditBusinessPage = lazy(() =>
  import('@/pages/EditBusinessPage').then((m) => ({ default: m.EditBusinessPage }))
);
const RegisterBusinessPage = lazy(() =>
  import('@/pages/RegisterBusinessPage').then((m) => ({ default: m.RegisterBusinessPage }))
);
const DealsPage = lazy(() => import('@/pages/DealsPage').then((m) => ({ default: m.DealsPage })));
const EventsPage = lazy(() =>
  import('@/pages/EventsPage').then((m) => ({ default: m.EventsPage }))
);
const EventDetailPage = lazy(() =>
  import('@/pages/EventDetailPage').then((m) => ({ default: m.EventDetailPage }))
);
const EditEventPage = lazy(() =>
  import('@/pages/EditEventPage').then((m) => ({ default: m.EditEventPage }))
);
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() =>
  import('@/pages/RegisterPage').then((m) => ({ default: m.RegisterPage }))
);
const SearchResultsPage = lazy(() =>
  import('@/pages/SearchResultsPage').then((m) => ({ default: m.SearchResultsPage }))
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const NotificationsPage = lazy(() =>
  import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage }))
);
const BookmarksPage = lazy(() =>
  import('@/pages/BookmarksPage').then((m) => ({ default: m.BookmarksPage }))
);
const MessagesPage = lazy(() =>
  import('@/pages/MessagesPage').then((m) => ({ default: m.MessagesPage }))
);
const PricingPage = lazy(() =>
  import('@/pages/PricingPage').then((m) => ({ default: m.PricingPage }))
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const AIAssistantPage = lazy(() =>
  import('@/pages/AIAssistantPage').then((m) => ({ default: m.AIAssistantPage }))
);
const HelpCenterPage = lazy(() => import('@/pages/HelpCenterPage'));
const DealDetailPage = lazy(() =>
  import('@/pages/DealDetailPage').then((m) => ({ default: m.DealDetailPage }))
);
const OffersPage = lazy(() =>
  import('@/pages/OffersPage').then((m) => ({ default: m.OffersPage }))
);
const OfferDetailPage = lazy(() =>
  import('@/pages/OfferDetailPage').then((m) => ({ default: m.OfferDetailPage }))
);
const VendorPortalPage = lazy(() =>
  import('@/pages/VendorPortalPage').then((m) => ({ default: m.VendorPortalPage }))
);
const CreateDealPage = lazy(() =>
  import('@/pages/CreateDealPage').then((m) => ({ default: m.CreateDealPage }))
);
const EditDealPage = lazy(() =>
  import('@/pages/EditDealPage').then((m) => ({ default: m.EditDealPage }))
);
const CreateEventPage = lazy(() =>
  import('@/pages/CreateEventPage').then((m) => ({ default: m.CreateEventPage }))
);
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const FAQPage = lazy(() => import('@/pages/FAQPage'));
const GuidelinesPage = lazy(() => import('@/pages/GuidelinesPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const CookiesPage = lazy(() => import('@/pages/CookiesPage'));
const DeleteAccountPage = lazy(() => import('@/pages/DeleteAccountPage'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));
const VerifyEmail = lazy(() => import('@/pages/auth/VerifyEmail'));
const SSOCallback = lazy(() => import('@/pages/auth/SSOCallback'));
const HubSsoLaunch = lazy(() => import('@/pages/auth/HubSsoLaunch'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminReports = lazy(() => import('@/pages/admin/AdminReports'));
const AdminAnalytics = lazy(() => import('@/pages/admin/AdminAnalytics'));
const AdminBusinesses = lazy(() => import('@/pages/admin/AdminBusinesses'));
const AdminContent = lazy(() => import('@/pages/admin/AdminContent'));
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'));
const AdminWorkflows = lazy(() => import('@/pages/admin/AdminWorkflows'));

function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
      Loading…
    </div>
  );
}

function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="ai-assistant" element={<AIAssistantPage />} />
          <Route path="forums" element={<ForumsPage />} />
          <Route path="forums/category/:slug" element={<ForumCategoryPage />} />
          <Route path="forums/posts/:slug" element={<PostPage />} />
          <Route path="forums/posts/:slug/edit" element={<EditPostPage />} />
          <Route path="forums/create" element={<CreatePostPage />} />
          <Route path="visa-experiences" element={<VisaExperiencesPage />} />
          <Route path="visa-experiences/create" element={<CreateVisaExperiencePage />} />
          <Route path="visa-experiences/:id" element={<VisaExperienceDetailPage />} />
          <Route path="visa-experiences/:id/edit" element={<EditVisaExperiencePage />} />
          <Route path="directory" element={<DirectoryPage />} />
          <Route path="directory/register" element={<RegisterBusinessPage />} />
          <Route path="directory/:id" element={<BusinessDetailPage />} />
          <Route path="directory/:id/edit" element={<EditBusinessPage />} />
          <Route path="deals" element={<DealsPage />} />
          <Route path="deals/create" element={<CreateDealPage />} />
          <Route path="deals/:dealId" element={<DealDetailPage />} />
          <Route path="deals/:dealId/edit" element={<EditDealPage />} />
          <Route path="offers" element={<OffersPage />} />
          <Route path="offers/:id" element={<OfferDetailPage />} />
          <Route path="vendor" element={<VendorPortalPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="events/create" element={<CreateEventPage />} />
          <Route path="events/:id" element={<EventDetailPage />} />
          <Route path="events/:id/edit" element={<EditEventPage />} />
          <Route path="users/:username" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="search" element={<SearchResultsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="bookmarks" element={<BookmarksPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="messages/:conversationId" element={<MessagesPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="help" element={<HelpCenterPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="guidelines" element={<GuidelinesPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="cookies" element={<CookiesPage />} />
          <Route path="delete-account" element={<DeleteAccountPage />} />
        </Route>

        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/sso/callback" element={<SSOCallback />} />
        <Route path="/auth/sso" element={<HubSsoLaunch />} />

        <Route
          path="/admin"
          element={
            <ProtectedAdminRoute>
              <AdminLayout />
            </ProtectedAdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route
            path="users"
            element={
              <ProtectedAdminRoute requiredRole="ADMIN">
                <AdminUsers />
              </ProtectedAdminRoute>
            }
          />
          <Route path="reports" element={<AdminReports />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="businesses" element={<AdminBusinesses />} />
          <Route path="content" element={<AdminContent />} />
          <Route
            path="settings"
            element={
              <ProtectedAdminRoute requiredRole="SUPER_ADMIN">
                <AdminSettings />
              </ProtectedAdminRoute>
            }
          />
          <Route path="workflows" element={<AdminWorkflows />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
